import { useCallback, useEffect, useRef, useState } from 'react';

export interface HoldToConfirmProps {
  /** Fired exactly once when the press is held for the full `holdMs`. */
  onConfirm: () => void;
  /** When true the button can't be pressed and an in-flight hold resets. */
  disabled?: boolean;
  /** Hold duration in ms before `onConfirm` fires. Default 5000. */
  holdMs?: number;
  /** Idle / default button label. */
  label?: string;
  /** Optional label shown while the press is being held. */
  holdingLabel?: string;
  dataTestId?: string;
  className?: string;
}

const DEFAULT_HOLD_MS = 5000;

/**
 * Press-and-hold confirm button. A destructive-intent control that requires a
 * sustained press (default 5s) before it fires — releasing early resets the
 * progress fill silently, no error. The fill width is driven by elapsed-hold
 * runtime state (not a design literal). Keyboard: Space / Enter hold while
 * pressed. Matches the `sync-diagnostics` "Clear local cache" pattern; consumed
 * by `/settings/organization` Delete-org.
 *
 * CSS namespace: `pn-hold-to-confirm` (`-fill` / `-label`).
 */
export function HoldToConfirm({
  onConfirm,
  disabled = false,
  holdMs = DEFAULT_HOLD_MS,
  label = 'Delete (hold 5s)',
  holdingLabel,
  dataTestId,
  className
}: HoldToConfirmProps) {
  const [progress, setProgress] = useState(0); // 0..1
  const [holding, setHolding] = useState(false);
  const rafRef = useRef<number | null>(null);
  const startRef = useRef(0);
  const firedRef = useRef(false);

  const stop = useCallback(() => {
    if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
    rafRef.current = null;
    setHolding(false);
    setProgress(0);
  }, []);

  const tick = useCallback(() => {
    const elapsed = Date.now() - startRef.current;
    const p = Math.min(1, elapsed / holdMs);
    setProgress(p);
    if (p >= 1) {
      if (!firedRef.current) {
        firedRef.current = true;
        onConfirm();
      }
      stop();
      return;
    }
    rafRef.current = requestAnimationFrame(tick);
  }, [holdMs, onConfirm, stop]);

  const start = useCallback(() => {
    if (disabled) return;
    firedRef.current = false;
    startRef.current = Date.now();
    setHolding(true);
    if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(tick);
  }, [disabled, tick]);

  useEffect(
    () => () => {
      if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
    },
    []
  );

  // Disabled mid-hold (e.g. the confirm-name field stopped matching) → reset.
  useEffect(() => {
    if (disabled && holding) stop();
  }, [disabled, holding, stop]);

  const tid = dataTestId ?? 'hold-to-confirm';

  return (
    <button
      type="button"
      className={
        className
          ? `pn-hold-to-confirm ${className}`
          : 'pn-hold-to-confirm'
      }
      disabled={disabled}
      data-testid={tid}
      data-holding={holding ? 'true' : 'false'}
      aria-label={label}
      onMouseDown={start}
      onMouseUp={stop}
      onMouseLeave={stop}
      onTouchStart={start}
      onTouchEnd={stop}
      onTouchCancel={stop}
      onKeyDown={(e) => {
        if ((e.key === ' ' || e.key === 'Enter') && !e.repeat) {
          e.preventDefault();
          start();
        }
      }}
      onKeyUp={(e) => {
        if (e.key === ' ' || e.key === 'Enter') stop();
      }}
    >
      <span
        className="pn-hold-to-confirm-fill"
        data-testid={`${tid}-fill`}
        style={{ width: `${Math.round(progress * 100)}%` }}
        aria-hidden="true"
      />
      <span className="pn-hold-to-confirm-label">
        {holding && holdingLabel ? holdingLabel : label}
      </span>
    </button>
  );
}
